import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  apiGet,
  setAccessTokenProvider,
  setAuthenticationFailureHandler,
} from "@/api/client";
import { hasAnyPermission as includesAnyPermission } from "@/auth/permissions";

export interface AuthoritativeIdentity {
  tenant_id: string;
  object_id: string;
  actor_type: "HUMAN" | "SERVICE";
  display_name: string | null;
  roles: string[];
  permissions: string[];
  client_id: string | null;
}

interface AuthContextValue {
  identity: AuthoritativeIdentity | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasAnyPermission: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const tenantId = process.env.REACT_APP_ENTRA_TENANT_ID?.trim() ?? "";
const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID?.trim() ?? "";
const apiScope = process.env.REACT_APP_ENTRA_API_SCOPE?.trim() ?? "";

const configurationError =
  !tenantId || !clientId || !apiScope
    ? "REACT_APP_ENTRA_TENANT_ID, REACT_APP_ENTRA_CLIENT_ID et REACT_APP_ENTRA_API_SCOPE sont requis."
    : null;

const msal = configurationError
  ? null
  : new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: { cacheLocation: "sessionStorage" },
    });

const scopes = apiScope ? [apiScope] : [];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [identity, setIdentity] = useState<AuthoritativeIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(configurationError);

  const accessToken = useCallback(async (): Promise<string | null> => {
    if (!msal || !account) return null;
    try {
      return (await msal.acquireTokenSilent({ account, scopes })).accessToken;
    } catch (tokenError) {
      if (tokenError instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect({ account, scopes });
        return null;
      }
      throw tokenError;
    }
  }, [account]);

  useEffect(() => {
    setAccessTokenProvider(account ? accessToken : null);
    return () => setAccessTokenProvider(null);
  }, [account, accessToken]);

  useEffect(() => {
    setAuthenticationFailureHandler(() => {
      setIdentity(null);
      setAccount(null);
      setError("Votre session a expiré. Reconnectez-vous avec Microsoft.");
    });
    return () => setAuthenticationFailureHandler(null);
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      if (!msal) {
        setLoading(false);
        return;
      }
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const selected = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        if (active) setAccount(selected);
      } catch {
        if (active) setError("Impossible d'initialiser l'authentification Microsoft Entra.");
      } finally {
        if (active) setLoading(false);
      }
    };
    void initialize();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!account) {
      setIdentity(null);
      return;
    }
    const loadIdentity = async () => {
      try {
        const result = await apiGet<AuthoritativeIdentity>("/auth/me");
        if (active) {
          setIdentity(result);
          setError(null);
        }
      } catch {
        if (active) setError("L'identité Entra n'a pas pu être autorisée par Rockfall.");
      }
    };
    void loadIdentity();
    return () => {
      active = false;
    };
  }, [account, accessToken]);

  const login = useCallback(async () => {
    if (!msal) return;
    await msal.loginRedirect({ scopes });
  }, []);

  const logout = useCallback(async () => {
    if (!msal) return;
    await msal.logoutRedirect({ account: account ?? undefined });
  }, [account]);

  const value = useMemo<AuthContextValue>(() => ({
    identity,
    loading,
    error,
    login,
    logout,
    hasAnyPermission: (...permissions: string[]) =>
      includesAnyPermission(identity?.permissions ?? [], permissions),
  }), [identity, loading, error, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth doit être utilisé dans AuthProvider.");
  return context;
};

export const AuthenticatedApp = ({ children }: { children: ReactNode }) => {
  const { identity, loading, error, login } = useAuth();
  if (loading) return <main className="grid min-h-screen place-items-center">Authentification…</main>;
  if (!identity) {
    return (
      <main className="grid min-h-screen place-items-center bg-background p-6">
        <section className="max-w-lg rounded-sm border border-border bg-card p-8 text-center">
          <img src="/rockfall-logo.png" alt="Rockfall" className="mx-auto mb-4 h-16 w-16 object-contain" />
          <h1 className="text-xl font-bold">Rockfall Operations Center</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {error ?? "Une identité Microsoft Entra autorisée est requise."}
          </p>
          {!configurationError && (
            <button type="button" onClick={() => void login()} className="ops-focus mt-6 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              Se connecter avec Microsoft
            </button>
          )}
        </section>
      </main>
    );
  }
  return <>{children}</>;
};
